import { useLazyQuery, useQuery } from '@apollo/client';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Box, Fab } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SubscriberDownloadReportFormatEnum } from '@/types/GraphqlSchema';
import { openDownloadUrl } from '../../components/Downloads/helpers';
import SnackbarContext from '../../contexts/SnackbarContext';
import { defaultDimensions } from '../../utils/analytics';
import { getUserId } from '../../utils/auth';
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENTS } from '../../utils/constants';
import { SECURE_SUBSCRIBER_DOWNLOAD_QUERY } from './operations';

const useStyles = makeStyles(theme => ({
  reportsButton: {
    ...theme.palette.button,
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(2),
    zIndex: 1,
    width: 'auto',
  },
}));

const SubscriberDownload = () => {
  const classes = useStyles();
  const { setSnack } = useContext(SnackbarContext);

  const { slug } = useParams();
  const [renderedReport, setReport] = useState(null);

  useQuery(SECURE_SUBSCRIBER_DOWNLOAD_QUERY, {
    variables: { format: SubscriberDownloadReportFormatEnum.Html, slug: slug },
    onCompleted: data => {
      if (data?.secureSubscriberDownload?.url) {
        const { url, downloadMessage } = data.secureSubscriberDownload;
        setSnack({
          open: true,
          message: downloadMessage,
          severity: 'success',
        });

        fetch(url)
          .then(res => {
            if (res.ok) {
              return res.text();
            } else {
              throw new Error('Not 2xx response');
            }
          })
          .then(html => {
            let processedHtml = html.replace(
              /#sidebar{background-color/gm,
              '#sidebar{display:none;background-color',
            );
            processedHtml = processedHtml.replace(
              /#page-container{position:absolute;top:0;left:0;/gm,
              '#page-container{',
            );

            setReport(processedHtml);
          })
          .catch(() => {
            setReport(
              'This report could not be rendered. Please try downloading this Intelligence Report to view.',
            );
          });
      } else {
        setReport('This report is not available.');
      }
    },
    onError: error => {
      setReport(error.message);
    },
  });
  const [getSecureDownload] = useLazyQuery(SECURE_SUBSCRIBER_DOWNLOAD_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const { url, filename, downloadMessage } = data.secureSubscriberDownload;
      setSnack({
        open: true,
        message: `Your download should begin shortly.${downloadMessage}`,
        severity: 'success',
      });
      openDownloadUrl(url, filename);
    },
    onError: error => {
      setSnack({
        severity: 'error',
        message: error.message,
        open: true,
      });
    },
  });

  const getDownloadUrl = () => {
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: 'ProprietaryDownloads',
      event_action: ANALYTICS_EVENT_ACTIONS.download,
      event_label: slug,
      userId: getUserId(),
      ...defaultDimensions,
    });
    getSecureDownload({
      variables: { format: SubscriberDownloadReportFormatEnum.Pdf, slug: slug },
    });
  };

  const subscriberDownloadHtml = () => {
    if (renderedReport) {
      return { __html: renderedReport };
    } else {
      return { __html: 'Loading report, please wait' };
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Fab
        onClick={() => {
          getDownloadUrl();
        }}
        disabled={renderedReport === null}
        className={classes.reportsButton}
        variant='extended'
      >
        <GetAppIcon fontSize='small' />
        &nbsp;download report
      </Fab>
      <Box
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Won't change to avoid breaking report rendering
        dangerouslySetInnerHTML={subscriberDownloadHtml()}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      />
    </Box>
  );
};

export default SubscriberDownload;
